/**
 * Proposals — what the firm has quoted, and what happened to it. Spec §H4, §H5.
 * Redrawn on the list shell in Wave 15E (C14 §10.6 items 1–2, ADR-0033): one
 * table, the status a filter and a column; a proposal opens in a dialog
 * (`ProposalDialog`), writing one is a dialog (`Composer`).
 *
 * The screen is arranged around the two things a firm does here: **write one
 * for a lead it has just qualified**, and **see which of the ones it sent came
 * back**. So the list is newest first and grouped by nothing — a pipeline is
 * the Leads screen's job, and a second one here would be two answers to "where
 * is this prospect".
 *
 * ## Sending asks first, and the link is shown once
 *
 * A sent proposal is a thing the firm told a prospect and it cannot be edited
 * afterwards, so *Send* confirms (ADR-0033 §1 — it did not before Wave 15).
 * `send` is the only call that returns the acceptance link (`lib/api/proposals`
 * says why). After a send the proposal's dialog opens with the link in a copy
 * field, because `prodesk.proposal.sent` is not registered in
 * `core-communication` (a platform ask, still open) so the email does not go out
 * — and a screen that said "Sent" while nothing was sent would be a firm's
 * prospect never hearing from them. The wording is `sendNote`'s.
 *
 * ## What the UI gate is, and is not
 *
 * FiveOX ADR-0029: enforce at UI **and** API level, so each of these is a
 * hidden control *and* a refusing route.
 *
 * * `lead:can_create` hides *Write a proposal*;
 * * `lead:can_update` hides Send;
 * * `lead:can_view_all` decides nothing here directly — the *service* narrows
 *   the list through the proposals' leads and hands back `meta.narrowed`, so
 *   this screen can say "yours" rather than silently showing a shorter list
 *   than a colleague sees.
 *
 * There is no delete. A proposal that was sent is a thing the firm told a
 * prospect; it expires or it is declined, and nothing here erases one
 * (constitution §4).
 */

import { Button, ConfirmDialog, formatDate } from '@fiveox/ui'
import { Plus, Send } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  useIsPhone,
  useListTable,
} from '../../../components/page'
import { LEAD, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { crm } from '../../../lib/api/crm'
import {
  PROPOSAL_STATUSES,
  type ProposalRecord,
  STATUS_LABELS,
  canSend,
  money,
  proposals as api,
  sendNote,
} from '../../../lib/api/proposals'
import { Composer } from './Composer'
import { ProposalDialog, type SentLink } from './ProposalDialog'

export function Proposals() {
  const { can } = useEvaluation()
  const phone = useIsPhone()
  const mayCreate = can(LEAD, 'can_create')
  const mayUpdate = can(LEAD, 'can_update')

  const [rows, setRows] = useState<ProposalRecord[]>([])
  const [leadNames, setLeadNames] = useState<Record<string, string>>({})
  const [narrowed, setNarrowed] = useState(false)
  const [composing, setComposing] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<ProposalRecord | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [sent, setSent] = useState<Record<string, SentLink>>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setError(null)
    try {
      const body = await api.list({ limit: 200 })
      setRows(body.data)
      setNarrowed(body.meta?.narrowed === true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The proposals could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // Who each proposal went to. A proposal carries its lead's id only; the
  // names are read once, and a failure leaves the column blank rather than
  // taking the list down with it.
  useEffect(() => {
    crm
      .list({ limit: 200 })
      .then((body) =>
        setLeadNames(Object.fromEntries(body.data.map((lead) => [lead.id, lead.full_name]))),
      )
      .catch(() => setLeadNames({}))
  }, [])

  const send = async () => {
    if (!confirming) return
    setSending(true)
    setSendError(null)
    try {
      const result = await api.send(confirming.id)
      setSent((prev) => ({
        ...prev,
        [confirming.id]: { note: sendNote(result), link: result.proposal.link ?? null },
      }))
      setOpen(confirming.id)
      setConfirming(null)
      await load()
    } catch (err) {
      // The one refusal worth its own sentence: the firm has no website address,
      // so an acceptance link has nowhere to point. The service says which field.
      setSendError(err instanceof ApiError ? err.message : 'That could not be sent.')
    } finally {
      setSending(false)
    }
  }

  const askToSend = (row: ProposalRecord) => {
    setSendError(null)
    setOpen(null)
    setConfirming(row)
  }

  const to = (row: ProposalRecord) => (row.lead_id ? (leadNames[row.lead_id] ?? null) : null)

  const columns = useMemo<ListColumn<ProposalRecord>[]>(
    () => [
      {
        key: 'title',
        label: 'Proposal',
        width: 240,
        required: true,
        phone: true,
        sortField: 'title',
        render: (row) => (
          <div className="min-w-0">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setOpen(row.id)
              }}
              className="text-foreground hover:text-primary focus-visible:ring-primary/40 rounded text-left font-medium focus-visible:ring-2 focus-visible:outline-none"
            >
              {row.title ?? 'Proposal'}
            </button>
            <p className="text-muted-foreground text-xs">
              {row.number ?? 'Not numbered yet — a draft'}
            </p>
          </div>
        ),
      },
      {
        key: 'to',
        label: 'To',
        width: 150,
        render: (row) => <span className="text-foreground">{to(row) ?? '—'}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        phone: true,
        sortField: 'status',
        render: (row) => (
          <div>
            <StatusBadge kind="proposal" status={row.status} />
            {row.status === 'declined' && row.decline_reason && (
              // The reason is the whole point of recording a decline: it is where
              // a firm finds out whether it is losing on price, timing or scope.
              <p className="text-muted-foreground mt-1 hidden max-w-[12rem] truncate text-xs sm:block">
                {row.decline_reason}
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'total',
        label: 'Total',
        width: 110,
        align: 'right',
        render: (row) => <span className="tabular-nums">{money(row.total_amount, row.currency)}</span>,
      },
      {
        key: 'valid_until',
        label: 'Valid until',
        width: 110,
        sortField: 'valid_until',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {row.valid_until ? formatDate(row.valid_until) : '—'}
          </span>
        ),
      },
      {
        key: 'created_at',
        label: 'Written',
        width: 110,
        sortField: 'created_at',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">{formatDate(row.created_at)}</span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leadNames],
  )

  const list = useListTable(rows, {
    columns,
    search: (row) => [row.title, row.number, to(row)],
    filters: [
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Every status',
        options: PROPOSAL_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] })),
        match: (row, value) => row.status === value,
      },
    ],
    initialSort: { field: 'created_at', dir: 'desc' },
  })

  const writeButton = mayCreate ? (
    <Button onClick={() => setComposing(true)}>
      <Plus size={16} aria-hidden /> Write a proposal
    </Button>
  ) : undefined

  const opened = open ? rows.find((row) => row.id === open) : undefined

  return (
    <>
      <ListPage
        title="Proposals"
        description={
          narrowed
            ? 'What you have quoted on your own leads, and what came back.'
            : 'What you have quoted, and what came back.'
        }
        primary={writeButton}
        loading={loading}
        error={error}
        isEmpty={list.isEmpty}
        empty={{
          title: 'Nothing quoted yet',
          description:
            'A proposal goes to a lead, prices itself from your catalog, and gives the prospect a link on your own website to accept it.',
          action: writeButton,
        }}
        toolbar={<ListToolbar list={list} placeholder="Search by title, number or lead" />}
      >
        <ListTable
          list={list}
          rowKey={(row) => row.id}
          onRowClick={(row) => setOpen(row.id)}
          trailing={
            mayUpdate
              ? {
                  header: <span className="sr-only">Send</span>,
                  width: phone ? 60 : 148,
                  cell: (row) =>
                    canSend(row) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={(event) => {
                          event.stopPropagation()
                          askToSend(row)
                        }}
                      >
                        <Send size={14} aria-hidden />
                        <span className="sr-only sm:not-sr-only">
                          {row.status === 'draft' ? 'Send' : 'Send again'}
                        </span>
                      </Button>
                    ) : null,
                }
              : undefined
          }
        />
      </ListPage>

      {opened && (
        <ProposalDialog
          row={opened}
          leadName={to(opened)}
          mayUpdate={mayUpdate}
          sent={sent[opened.id] ?? null}
          onClose={() => setOpen(null)}
          onSend={() => askToSend(opened)}
        />
      )}

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming?.status === 'draft' ? 'Send this proposal?' : 'Send it again?'}
        confirmLabel={confirming?.status === 'draft' ? 'Send' : 'Send again'}
        cancelLabel="Not yet"
        onConfirm={() => void send()}
        busy={sending}
        busyLabel="Sending…"
        error={sendError}
      >
        {confirming && (
          <>
            <p>
              <span className="text-foreground font-medium">{confirming.title ?? 'This proposal'}</span>
              {to(confirming) ? ` goes to ${to(confirming)}` : ' goes to its lead'}, for{' '}
              {money(confirming.total_amount, confirming.currency)}, with a link on your website to
              accept or decline it.
            </p>
            {confirming.status === 'draft' ? (
              <p>Once it is sent it cannot be edited — the prospect’s link and this proposal are the same thing.</p>
            ) : (
              <p>It keeps the same number and the same link, so the copy they already have still works.</p>
            )}
          </>
        )}
      </ConfirmDialog>

      <Composer
        open={composing}
        onClose={() => setComposing(false)}
        onSaved={() => {
          setComposing(false)
          void load()
        }}
      />
    </>
  )
}
